import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Plus, X, AlertTriangle, CheckCircle2, Archive, ArchiveRestore, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, apiReferences, downloadFile } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import AllergenChips from "@/components/AllergenChips";
import AIPanel from "@/components/AIPanel";
import NutritionPanel from "@/components/NutritionPanel";
import AttachmentsCard from "@/components/AttachmentsCard";
import CheaperTwinCard from "@/components/CheaperTwinCard";
import SensoryRadar from "@/components/SensoryRadar";
import FormulaHistory from "@/components/FormulaHistory";
import DeleteBlockedModal from "@/components/DeleteBlockedModal";
import Modal, { inputCls, btnPrimary, btnSecondary, Field } from "@/components/Modal";
import { FormulaForm } from "@/pages/Formulas";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { inr, fmtDateTime, label } from "@/lib/constants";

const TABS = ["composition", "nutrition", "history"];

export default function FormulaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("composition");
  const [showEdit, setShowEdit] = useState(false);
  const [lineModal, setLineModal] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["formula", id],
    queryFn: async () => (await api.get(`/formulas/${id}/full`)).data,
  });

  const { data: ingredientsLib } = useQuery({
    queryKey: ["ingredients"],
    queryFn: async () => (await api.get("/ingredients", { params: { limit: 500 } })).data,
  });

  const { data: advice } = useQuery({
    queryKey: ["formula-advice", id],
    queryFn: async () => (await api.get(`/ai/formulas/${id}/advisor`)).data,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["formula", id] });
    queryClient.invalidateQueries({ queryKey: ["formula-versions", id] });
    queryClient.invalidateQueries({ queryKey: ["nutrition", id] });
  };

  const deleteFormula = async () => {
    const ok = await confirm({
      title: "Delete formula",
      message: `Delete "${formula?.name}" and its ingredient lines? This cannot be undone.`,
      confirmLabel: "Delete formula",
    });
    if (!ok) return;
    try {
      await api.delete(`/formulas/${id}`);
      toast.success("Formula deleted");
      navigate("/formulas");
    } catch (err) {
      const references = apiReferences(err);
      if (references) setBlocked({ references, message: apiError(err) });
      else toast.error(apiError(err));
    }
  };

  const setArchived = async (archived) => {
    setArchiving(true);
    try {
      await api.post(`/formulas/${id}/archive`, { archived });
      toast.success(archived ? "Formula archived" : "Formula restored");
      setBlocked(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setArchiving(false);
    }
  };

  const downloadDatasheet = async (variant) => {
    try {
      await downloadFile(`/formulas/${id}/datasheet.pdf`, `smoor-datasheet-${variant}.pdf`,
                         { variant }, "application/pdf");
      toast.success(`${variant === "internal" ? "Internal" : "Customer"} data sheet downloaded`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeLine = async (line) => {    const ok = await confirm({
      title: "Remove ingredient line",
      message: `Remove ${line.ingredient_name} (${line.percentage}%) from this formula? A new version snapshot will be saved.`,
      confirmLabel: "Remove line",
    });
    if (!ok) return;
    try {
      await api.delete(`/formulas/${id}/ingredients/${line.id}`);
      toast.success("Ingredient removed");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (isLoading || !data) return <div className="text-[#8C7A6B] text-sm" data-testid="formula-detail-loading">Loading formula…</div>;
  const { formula, ingredients, costing, percentage_sum: totalPct, composition_ok: ok, allergens } = data;
  const bomCost = ingredients.reduce((s, l) => s + l.line_cost_per_kg, 0);

  return (
    <div data-testid="formula-detail-page">
      <Link to="/formulas" className="inline-flex items-center gap-2 text-sm text-[#8C7A6B] hover:text-[#2A1810] mb-4" data-testid="back-to-formulas">
        <ArrowLeft size={15} /> Back to formulas
      </Link>

      <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm mb-6" data-testid="formula-header">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl font-bold text-[#1C100B]" data-testid="formula-name">{formula.name}</h1>
              <StatusBadge value={formula.status} testid="formula-status" />
              {formula.archived && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]" data-testid="formula-archived-badge">archived</span>
              )}
              {ok ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]" data-testid="composition-ok-badge">
                  <CheckCircle2 size={12} /> Composition {totalPct}%
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" data-testid="composition-warning-badge">
                  <AlertTriangle size={12} /> Lines sum to {totalPct}% — should be 100% ± 0.5
                </span>
              )}
            </div>
            <div className="mt-3" data-testid="formula-allergen-rollup">
              <span className="text-xs font-semibold tracking-wider uppercase text-[#8C7A6B] mr-2">Allergens</span>
              <span className="inline-flex align-middle"><AllergenChips allergens={allergens} empty="none declared" /></span>
            </div>
            <p className="text-sm text-[#5C4A3E] mt-3 max-w-2xl leading-relaxed">{formula.description || "No description."}</p>
            <div className="flex gap-6 mt-4 text-xs text-[#8C7A6B] flex-wrap">
              <span>Category: <span className="text-[#2A1810] font-medium">{label(formula.category)}</span></span>
              <span>Version: <span className="text-[#2A1810] font-medium">v{formula.version}</span></span>
              <span>Created by: <span className="text-[#2A1810] font-medium">{formula.created_by_name}</span></span>
            </div>
            <div className="text-xs text-[#8C7A6B] mt-2" data-testid="formula-last-edited">
              Last edited by <span className="text-[#2A1810] font-medium">{formula.updated_by_name || "—"}</span> on{" "}
              <span className="text-[#2A1810] font-medium">{fmtDateTime(formula.updated_at)}</span>
            </div>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <button onClick={() => downloadDatasheet("customer")} className={btnSecondary} data-testid="datasheet-customer-button">
              <FileDown size={14} className="inline mr-1.5 -mt-0.5" /> Customer PDF
            </button>
            {canWrite && (
              <>
                <button onClick={() => downloadDatasheet("internal")} className={btnSecondary} data-testid="datasheet-internal-button">
                  <FileDown size={14} className="inline mr-1.5 -mt-0.5" /> Internal PDF
                </button>
                <button onClick={() => setShowEdit(true)} className={btnSecondary} data-testid="edit-formula-button"><Pencil size={14} className="inline mr-1.5 -mt-0.5" /> Edit</button>
              <button onClick={() => setArchived(!formula.archived)} disabled={archiving} className={btnSecondary} data-testid="archive-formula-button">
                {formula.archived ? <ArchiveRestore size={14} className="inline mr-1.5 -mt-0.5" /> : <Archive size={14} className="inline mr-1.5 -mt-0.5" />}
                {formula.archived ? "Restore" : "Archive"}
              </button>
              <button onClick={deleteFormula} className="bg-[#FEF2F2] hover:bg-[#FEE2E2] text-[#B91C1C] border border-[#FECACA] px-4 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2" data-testid="delete-formula-button">
                <Trash2 size={14} /> Delete
              </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#E8DFD1] mb-6" data-testid="formula-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`formula-tab-${t}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-[1px] ${
              tab === t
                ? "border-[#C5A059] text-[#2A1810] font-bold bg-[#FAF6F0]/50 rounded-t-lg"
                : "border-transparent text-[#8C7A6B] hover:text-[#2A1810]"
            }`}
          >
            {label(t)}{t === "history" && data.version_count ? ` (${data.version_count})` : ""}
          </button>
        ))}
      </div>

      {tab === "history" && <FormulaHistory formulaId={id} onRestored={refresh} />}

      {tab === "nutrition" && <NutritionPanel formulaId={id} formula={formula} onServingSaved={refresh} />}

      {tab === "composition" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="formula-ingredients-card">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#F4EFEA]">
                <h2 className="font-display text-lg font-semibold text-[#1C100B]">Ingredients</h2>
                {canWrite && (
                  <button onClick={() => setLineModal({})} className={`${btnSecondary} !py-2`} data-testid="add-ingredient-button">
                    <Plus size={14} className="inline mr-1.5 -mt-0.5" /> Add ingredient
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Ingredient</th>
                      <th className="px-4 py-3 text-left font-semibold">Type</th>
                      <th className="px-4 py-3 text-left font-semibold">Supplier</th>
                      <th className="px-4 py-3 text-right font-semibold">%</th>
                      <th className="px-4 py-3 text-right font-semibold">Cost/kg share</th>
                      <th className="px-4 py-3 text-left font-semibold">Allergens</th>
                      {canWrite && <th className="px-4 py-3" />}
                    </tr>
                  </thead>
                  <tbody>
                    {ingredients.map((l) => (
                      <tr key={l.id} className="border-b border-[#F4EFEA] last:border-0" data-testid={`ingredient-line-${l.id}`}>
                        <td className="px-4 py-3 font-medium text-[#2A1810]">{l.ingredient_name}</td>
                        <td className="px-4 py-3 text-[#5C4A3E]">{label(l.ingredient_type)}</td>
                        <td className="px-4 py-3 text-[#5C4A3E]">{l.supplier_name}</td>
                        <td className="px-4 py-3 text-right text-[#2A1810] font-medium">{l.percentage}%</td>
                        <td className="px-4 py-3 text-right text-[#5C4A3E]">{inr(l.line_cost_per_kg)}</td>
                        <td className="px-4 py-3"><AllergenChips allergens={l.allergen_flags} /></td>
                        {canWrite && (
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1.5">
                              <button onClick={() => setLineModal(l)} className="p-1.5 rounded hover:bg-[#FAF6F0] text-[#5C4A3E]" data-testid={`edit-ingredient-line-${l.id}`}><Pencil size={13} /></button>
                              <button onClick={() => removeLine(l)} className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#B91C1C]" data-testid={`remove-ingredient-${l.id}`}><X size={14} /></button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {ingredients.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="ingredients-empty">No ingredients yet.</td></tr>
                    )}
                  </tbody>
                  {ingredients.length > 0 && (
                    <tfoot className="bg-[#FAF6F0] border-t border-[#E8DFD1]">
                      <tr>
                        <td className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#2A1810]" colSpan={3}>Batch totals</td>
                        <td className={`px-4 py-3 text-right font-bold ${ok ? "text-[#2A1810]" : "text-[#92400E]"}`} data-testid="total-percentage">{totalPct.toFixed(1)}%</td>
                        <td className="px-4 py-3 text-right font-bold text-[#2A1810]" data-testid="bom-cost">{inr(bomCost)}/kg</td>
                        <td colSpan={canWrite ? 2 : 1} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            <AttachmentsCard basePath={`/formulas/${id}`} scope="formula" />

            <SensoryProfileCard formulaId={id} />

            <CheaperTwinCard formulaId={id} />

            <AIPanel
              title="AI Formula Advisor"
              description="Reformulation ideas based on this formula's lines, real ingredient costs, allergens and latest sensory scores."
              endpoint={`/ai/formulas/${id}/advisor`}
              cacheKey={`formula-${id}`}
              initial={advice}
              testid="ai-formula-advisor"
            />
          </div>

          <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm h-fit" data-testid="costing-summary-card">
            <h2 className="font-display text-lg font-semibold text-[#1C100B] mb-4">Cost & Margin</h2>
            {costing.length === 0 && <p className="text-sm text-[#8C7A6B]" data-testid="costing-empty">No costing scenarios for this formula yet.</p>}
            <div className="space-y-4">
              {costing.map((c) => (
                <div key={c.id} className="border border-[#F4EFEA] rounded-lg p-4" data-testid={`costing-card-${c.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#8C7A6B]">{label(c.channel)}</span>
                    <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${c.contribution_margin_percent >= 60 ? "text-[#15803D] bg-[#F0FDF4]" : c.contribution_margin_percent >= 45 ? "text-[#B45309] bg-[#FFFBEB]" : "text-[#B91C1C] bg-[#FEF2F2]"}`}>
                      {c.contribution_margin_percent}%
                    </span>
                  </div>
                  <div className="text-xs text-[#8C7A6B] mb-2">{c.packaging_name}</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-[10px] uppercase text-[#8C7A6B]">Cost</div><div className="text-sm font-semibold text-[#2A1810]">{inr(c.total_cost_per_unit)}</div></div>
                    <div><div className="text-[10px] uppercase text-[#8C7A6B]">Price</div><div className="text-sm font-semibold text-[#2A1810]">{inr(c.proposed_retail_price)}</div></div>
                    <div><div className="text-[10px] uppercase text-[#8C7A6B]">Margin</div><div className="text-sm font-semibold text-[#15803D]">{inr(c.contribution_margin_absolute)}</div></div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/packaging" className="block mt-4 text-xs font-semibold text-[#B08B46] hover:underline" data-testid="manage-costing-link">
              Manage costing scenarios →
            </Link>
          </div>
        </div>
      )}

      {showEdit && <FormulaForm initial={formula} onClose={() => setShowEdit(false)} onSaved={refresh} />}

      {lineModal && (
        <LineForm
          formulaId={id}
          initial={lineModal.id ? lineModal : null}
          ingredients={ingredientsLib || []}
          onClose={() => setLineModal(null)}
          onSaved={refresh}
        />
      )}

      {blocked && (
        <DeleteBlockedModal
          message={blocked.message}
          references={blocked.references}
          onClose={() => setBlocked(null)}
          onArchive={() => setArchived(true)}
          archiving={archiving}
        />
      )}
    </div>
  );
}

function SensoryProfileCard({ formulaId }) {
  const { data } = useQuery({
    queryKey: ["sensory-profile", formulaId],
    queryFn: async () => (await api.get(`/formulas/${formulaId}/sensory-profile`)).data,
  });

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="sensory-profile-card">
      <div className="flex items-center justify-between gap-2 px-6 py-4 border-b border-[#F4EFEA]">
        <h2 className="font-display text-lg font-semibold text-[#1C100B]">Taste Profile</h2>
        {data?.count ? <span className="text-xs text-[#8C7A6B]">{data.count} panel{data.count > 1 ? "s" : ""}</span> : null}
      </div>
      <div className="px-4 py-4">
        {data ? <SensoryRadar series={[{ name: data.formula_name, scores: data }]} height={280} /> : null}
      </div>
    </div>
  );
}


function LineForm({ formulaId, initial, ingredients, onClose, onSaved }) {
  const [line, setLine] = useState(
    initial
      ? { ingredient_id: initial.ingredient_id, percentage: initial.percentage, yield_factor: initial.yield_factor ?? 1, notes: initial.notes || "", change_note: "" }
      : { ingredient_id: "", percentage: "", yield_factor: 1, notes: "", change_note: "" }
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setLine({ ...line, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        formula_id: formulaId,
        ingredient_id: line.ingredient_id,
        percentage: Number(line.percentage),
        yield_factor: Number(line.yield_factor) || 1,
        notes: line.notes,
        change_note: line.change_note,
      };
      if (initial?.id) {
        await api.put(`/formulas/${formulaId}/ingredients/${initial.id}`, payload);
        toast.success("Ingredient line updated");
      } else {
        await api.post(`/formulas/${formulaId}/ingredients`, payload);
        toast.success("Ingredient added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial ? "Edit Ingredient Line" : "Add Ingredient"} onClose={onClose} testid="add-ingredient-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="add-ingredient-form">
        <Field label="Ingredient">
          <select data-testid="line-ingredient-select" required value={line.ingredient_id} onChange={set("ingredient_id")} className={inputCls}>
            <option value="">Select ingredient…</option>
            {ingredients.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Percentage">
            <input data-testid="line-percentage-input" type="number" step="0.1" min={0} max={100} required value={line.percentage} onChange={set("percentage")} className={inputCls} />
          </Field>
          <Field label="Yield factor">
            <input data-testid="line-yield-input" type="number" step="0.01" min={0.01} max={10} value={line.yield_factor} onChange={set("yield_factor")} className={inputCls} />
          </Field>
        </div>
        <Field label="Notes">
          <input data-testid="line-notes-input" value={line.notes} onChange={set("notes")} className={inputCls} />
        </Field>
        <Field label="Change note (saved with this version)">
          <input data-testid="line-change-note-input" value={line.change_note} onChange={set("change_note")} className={inputCls} placeholder="Optional" />
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="line-cancel-button">Cancel</button>
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="line-save-button">{saving ? "Saving…" : initial ? "Save line" : "Add"}</button>
        </div>
      </form>
    </Modal>
  );
}
